/*
This file is intended to reduce dependecies between FormFlowActivity/ActivityInvokers and FormFlow implementation files.
It currently only contains types required for TaskPresenter but will be updated to contain all exported types in an upcoming workflow.
*/
import type {
  ActivityBasedFormFlowDefinition,
  CodedFormFlowDefinition,
  ControlTransitionDefinitionMapping,
  FormDefinitionMapping,
  FormFlowActivity,
  FormFlowVariable,
} from "@wtg-glow/form-flow-engine";
import type { ResourceInfo } from "CaptionService";
import { TaskMessageBusEvent } from "Constants";
import type FormFlowOthersModel from "FormFlowOthersModel";
import { MessageType, type MessageBus } from "MessageBus";
import type ModelProvider from "ModelProvider";
import type { PageExtensions } from "PageExtensions";
import type { TaskViewModel } from "TaskViewModel";
import type { Shell, ShellOptions } from "UIService";
import type { WorkflowContext } from "VueHooks/WorkflowContextHook";
import type FinderRelatedCollectionStrategy from "Widgets/gwFinderRelatedCollectionStrategy";
import type FinderStandaloneStrategy from "Widgets/gwFinderStandaloneStrategy";
import type { PureComputed } from "knockout";
import { type WtgFrameworkTaskGenericActionPlacement } from "wtg-material-ui";

export type ShellProviderFn = (formId: string, shellOptions: TaskShellOptions) => Promise<Shell>;

export type ShellDisposeFn = () => void;

export type TaskPresenterComputedOptions = {
  canShowDocuments: PureComputed<boolean>;
  canShowEDocs: PureComputed<boolean>;
  canShowLogs: PureComputed<boolean>;
  canShowMessages: boolean;
  canShowNotes: PureComputed<boolean>;
  canShowWorkflow: PureComputed<boolean>;
};

export interface TaskPageExtensions<T = unknown> extends PageExtensions {
  dataItem: T;
  messageBus: MessageBus;
  workflowContext: WorkflowContext;
  modelProvider?: ModelProvider;
  sessionData?: {
    configurationParentID?: string;
    clientPK?: string;
  };
}

export interface TaskShellOptions extends ShellOptions {
  pageExtensions: TaskPageExtensions;
  viewModel: TaskViewModel;
  transitionResolveHandler?: () => void;
  hideSaveCloseButtons?: boolean;
  disableSaveCancel?: boolean;
  others?: FormFlowOthersModel;
  onCompletedUri?: string;
  configurationParentID?: string;
  clientPK?: string;
}

export interface UIExtension {
  applyAsync(): Promise<void>;
}

export type UIExtensionSettings = [extension: UIExtension, runWhenIdle: boolean];

export type { FormFlowActivity, FormFlowVariable };

export type FormFlowAction = {
  id: string;
  caption?: string;
  onInvoke: () => Promise<unknown>;
};

type ExtendedFormFlowDefinition = {
  Caption: ResourceInfo;
  EntityTypeName?: string;
  DefaultModel?: string;
  DisableAddToRecents?: boolean;
  DisableRefreshOnClose?: boolean;
  CodedFormFlowPath?: string;
};

export type ExtendedActivityBasedFormFlowDefinition = ActivityBasedFormFlowDefinition & ExtendedFormFlowDefinition;
export type ExtendedCodedFormFlowDefinition = CodedFormFlowDefinition &
  ExtendedFormFlowDefinition & { CodedFormFlowPath: string };
export type FormFlowDefinition = ExtendedActivityBasedFormFlowDefinition | ExtendedCodedFormFlowDefinition;

export type FormFlowDefinitionProviderFn = (pk: string) => Promise<FormFlowDefinition>;

export type FormFlowAlias = {
  Name: string;
  Module: string;
};

export interface Transition {
  Kind?: string;
  NextActivityId: string;
  SuspendValidation?: boolean;
}

export interface FooterTransition extends Transition {
  Id: string;
  Caption: ResourceInfo;
  VisibilityExpression?: string;
  DisableExpression?: string;
  Category?: ResourceInfo;
  ConditionId?: string;
  Condition?: string;
  Placement?: WtgFrameworkTaskGenericActionPlacement;
  Icon?: string;
  Tooltip?: ResourceInfo;
  TooltipWhenDisabled?: ResourceInfo;
}

export interface ControlTransition extends Transition {
  ControlId: ControlTransitionDefinitionMapping<keyof FormDefinitionMapping>;
}

export interface ButtonTransition extends ControlTransition {
  caption: string;
}

export interface TextBoxTransition extends ControlTransition {
  IsGs1VariableName: string;
  TextVariableName: string;
}

export interface LookupTransition extends ControlTransition {
  ResultVariableName: string;
}

export interface FinderTransition extends ControlTransition {
  isGS1: boolean;
  SearchTermVariableName: string;
  ExpandPaths: string[];
  ResultsVariableName: string;
}

export function isControlTransition(transition: Transition): transition is ControlTransition {
  return "ControlId" in transition;
}

export function isButtonTransition(transition: Transition): transition is ButtonTransition {
  return transition.Kind === "Button";
}

export function isTextBoxTransition(transition: Transition): transition is TextBoxTransition {
  return transition.Kind === "TextBox";
}

export function isFinderTransition(transition: Transition): transition is FinderTransition {
  return transition.Kind === "Finder";
}

export function isLookupTransition(transition: Transition): transition is LookupTransition {
  return transition.Kind === "Lookup";
}

export function isFooterTransition(transition: Transition): transition is FooterTransition {
  return transition.Kind === "Footer";
}

export interface ControlTransitionEventArgs {
  controlId: string;
  isHandled: boolean;
}

export interface FinderTransitionEventArgs extends ControlTransitionEventArgs {
  searchTerm: string;
  isGS1: boolean;
  strategy: FinderRelatedCollectionStrategy | FinderStandaloneStrategy;
}

export interface TextBoxTransitionEventArgs extends ControlTransitionEventArgs {
  text: string;
  isGs1: boolean;
}

export interface LookUpTransitionEventArgs extends ControlTransitionEventArgs {
  entityName: string;
  entityPK: string;
}

export function isFinderTransitionEventArgs(
  eventArgs: ControlTransitionEventArgs,
): eventArgs is FinderTransitionEventArgs {
  return "strategy" in eventArgs;
}

export function isTextBoxTransitionEventArgs(
  eventArgs: ControlTransitionEventArgs,
): eventArgs is TextBoxTransitionEventArgs {
  return "text" in eventArgs;
}

export function isLookupTransitionEventArgs(
  eventArgs: ControlTransitionEventArgs,
): eventArgs is LookUpTransitionEventArgs {
  return "entityName" in eventArgs;
}

export type PendingActionsHandler = {
  handlePendingActionsAsync(): Promise<unknown>;
};

export type SubFormFlowEventArgs = {
  entityPK?: string;
  entityName?: string;
  entityPKs?: string[];
  headerEntityName?: string;
  headerEntityPK?: string;
  formFlowPK: string;
  others?: FormFlowOthersModel;
  CreateNewSession?: boolean;
  ShowInDialog?: boolean;
  ShowInDrawer?: boolean;
  ShowMaximizedDialog?: boolean;
  SuspendValidation?: boolean;
  DisableSaveCurrentSession?: boolean;
};

export interface FormFlowResultData {
  formFlowPK?: string;
  entityPK?: string;
  entityPKs?: string[];
}

export type PendingChangesHandler = { handlePendingActionsAsync(): Promise<unknown> };

export const refreshFormMessageType = new MessageType(TaskMessageBusEvent.RefreshForm);
export const cancelTaskMessageType = new MessageType(TaskMessageBusEvent.CancelTask);
export const invokeSubFormFlowMessageType = new MessageType<SubFormFlowEventArgs, Promise<FormFlowResultData | void>>(
  TaskMessageBusEvent.InvokeSubFormFlow,
);
export const invokeControlTransitionMessageType = new MessageType<ControlTransitionEventArgs, Promise<void>>(
  TaskMessageBusEvent.InvokeControlTransition,
);
export const registerPendingActionHandlerMessageType = new MessageType<PendingChangesHandler, Promise<void>>(
  TaskMessageBusEvent.RegisterPendingActionHandler,
);
export const unregisterPendingActionHandlerMessageType = new MessageType<PendingChangesHandler, Promise<void>>(
  TaskMessageBusEvent.UnregisterPendingActionHandler,
);
export const ensureSavedMessageType = new MessageType<{ shouldRunExclusive?: boolean }, boolean>(
  TaskMessageBusEvent.EnsureSaved,
);
